import { useEffect, useRef, useState } from 'react'
import { useInfiniteQuery } from '@tanstack/react-query'
import { activitiesApi, type Activity } from '../api/activities'
import { type LeadCall } from '../api/recordings'
import { formatDateTime, formatDuration, fullName } from '../lib/utils'
import { formatDateTime as formatVisitAt, formatDateOnly as formatVisitDay } from '../lib/scheduling'
import { PageSpinner } from './ui/Spinner'
import { RecordingPlayer } from './RecordingPlayer'

const tail9 = (n: unknown) => String(n ?? '').replace(/\D/g, '').slice(-9)

// Link a call_made activity to its recording by dialed number + nearest timestamp.
// (Works for all calls — old activities lack a stored callLogId, but every activity
// has its toNumber + createdAt, which line up with the call log they were made for.)
function recordingIdForCall(activity: Activity, calls?: LeadCall[]): string | null {
  if (activity.type !== 'call_made' || !calls?.length) return null
  const num = tail9(activity.metadata?.['toNumber'])
  if (!num) return null
  const t = new Date(activity.createdAt).getTime()
  let bestId: string | null = null
  let bestDiff = Infinity
  for (const c of calls) {
    if (!c.recordingId || tail9(c.toNumber) !== num) continue
    const d = Math.abs(new Date(c.createdAt).getTime() - t)
    if (d < bestDiff) { bestDiff = d; bestId = c.recordingId }
  }
  return bestDiff <= 120000 ? bestId : null   // within 2 minutes = same call
}
import {
  Mail, MessageSquare, Phone, StickyNote, CheckSquare, ArrowRight,
  UserCheck, AlertTriangle, Inbox, CalendarPlus, CalendarClock, CalendarX,
} from 'lucide-react'

/**
 * When a logged appointment happens, formatted in the reader's own timezone —
 * the activity payload carries raw instants precisely so this stays local.
 */
function visitWhen(meta: Record<string, unknown>): string | null {
  if (meta['isAllDay']) {
    const day = meta['allDayDate']
    return day ? `${formatVisitDay(String(day))} · All day` : null
  }
  const start = meta['startAt']
  if (!start) return null
  const label = formatVisitAt(String(start))
  const end = meta['endAt']
  if (!end) return label
  const endTime = new Date(String(end)).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  return `${label} – ${endTime}`
}

const typeIcon: Record<string, React.ReactNode> = {
  email_sent: <Mail className="w-3.5 h-3.5" />,
  email_received: <Inbox className="w-3.5 h-3.5" />,
  comment: <MessageSquare className="w-3.5 h-3.5" />,
  note: <StickyNote className="w-3.5 h-3.5" />,
  call_made: <Phone className="w-3.5 h-3.5" />,
  task_created: <CheckSquare className="w-3.5 h-3.5" />,
  task_completed: <CheckSquare className="w-3.5 h-3.5 text-green-600" />,
  status_changed: <ArrowRight className="w-3.5 h-3.5" />,
  assigned: <UserCheck className="w-3.5 h-3.5" />,
  sla_breached: <AlertTriangle className="w-3.5 h-3.5 text-red-500" />,
  whatsapp_received: <MessageSquare className="w-3.5 h-3.5 text-green-600" />,
  appointment_created: <CalendarPlus className="w-3.5 h-3.5" />,
  appointment_updated: <CalendarClock className="w-3.5 h-3.5" />,
  appointment_deleted: <CalendarX className="w-3.5 h-3.5" />,
}

const typeColor: Record<string, string> = {
  email_sent: 'bg-blue-100 text-blue-600',
  email_received: 'bg-blue-100 text-blue-600',
  comment: 'bg-purple-100 text-purple-600',
  note: 'bg-yellow-100 text-yellow-600',
  call_made: 'bg-green-100 text-green-600',
  task_created: 'bg-gray-100 text-gray-600',
  task_completed: 'bg-green-100 text-green-600',
  status_changed: 'bg-gray-100 text-gray-600',
  assigned: 'bg-indigo-100 text-indigo-600',
  sla_breached: 'bg-red-100 text-red-600',
  appointment_created: 'bg-teal-100 text-teal-600',
  appointment_updated: 'bg-teal-100 text-teal-600',
  appointment_deleted: 'bg-red-100 text-red-600',
}

// For task_created, color the icon by task type (meeting = indigo, reminder = amber).
function iconColorClass(activity: Activity): string {
  if (activity.type === 'task_created') {
    return activity.metadata?.['taskType'] === 'meeting'
      ? 'bg-indigo-100 text-indigo-600'
      : 'bg-amber-100 text-amber-600'
  }
  return typeColor[activity.type] || 'bg-gray-100 text-gray-600'
}

interface Props {
  companyId: string
  refType: string
  refId: string
  /** The lead's calls (with recordingId where available); call entries match to one by number+time. */
  calls?: LeadCall[]
}

const INITIAL_COUNT = 20 // first page
const MORE_COUNT = 10     // each subsequent page (on scroll)

const CONTENT_LIMIT = 100

function TimelineContent({ text }: { text: string }) {
  const [expanded, setExpanded] = useState(false)
  if (text.length <= CONTENT_LIMIT) {
    return <p className="text-sm text-gray-700 whitespace-pre-wrap">{text}</p>
  }
  return (
    <p className="text-sm text-gray-700 whitespace-pre-wrap">
      {expanded ? text : `${text.slice(0, CONTENT_LIMIT)}…`}{' '}
      <button
        type="button"
        onClick={() => setExpanded(e => !e)}
        className="text-primary-600 hover:underline font-medium"
      >
        {expanded ? 'See Less' : 'See More'}
      </button>
    </p>
  )
}

export function ActivityTimeline({ companyId, refType, refId, calls }: Props) {
  const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ['activities', companyId, refType, refId],
    initialPageParam: 0,
    queryFn: ({ pageParam }) =>
      activitiesApi.list(companyId, refType, refId, { offset: pageParam, limit: pageParam === 0 ? INITIAL_COUNT : MORE_COUNT }),
    getNextPageParam: (_last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.data.length, 0)
      const total = pages[0]?.meta.total ?? 0
      return loaded < total ? loaded : undefined
    },
  })

  // Auto-load more when the sentinel scrolls into view.
  const sentinelRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const el = sentinelRef.current
    if (!el || !hasNextPage) return
    const obs = new IntersectionObserver(
      entries => { if (entries[0].isIntersecting && !isFetchingNextPage) fetchNextPage() },
      { rootMargin: '120px' },
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [hasNextPage, isFetchingNextPage, fetchNextPage])

  const activities = data?.pages.flatMap(p => p.data) ?? []

  if (isLoading) return <PageSpinner />
  if (activities.length === 0) {
    return <p className="text-sm text-gray-500 text-center py-8">No activity yet</p>
  }

  return (
    <div className="flow-root">
      <ul className="-mb-8">
        {activities.map((activity: Activity, idx: number) => (
          <li key={activity.id}>
            <div className="relative pb-8">
              {idx < activities.length - 1 && (
                <span className="absolute left-4 top-4 -ml-px h-full w-0.5 bg-gray-200" aria-hidden />
              )}
              <div className="relative flex items-start gap-3">
                <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center ${iconColorClass(activity)}`}>
                  {typeIcon[activity.type] || <MessageSquare className="w-3.5 h-3.5" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5">
                    {activity.performedByUser && (
                      <span className="text-sm font-medium text-gray-900">
                        {fullName(activity.performedByUser.firstName, activity.performedByUser.lastName)}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{formatDateTime(activity.createdAt)}</span>
                  </div>
                  {activity.content && <TimelineContent text={activity.content} />}
                  {activity.metadata && Object.keys(activity.metadata).length > 0 && (
                    <div className="mt-1 text-xs text-gray-500 flex items-center gap-2 flex-wrap">
                      {activity.metadata['subject'] != null && <span>Subject: {String(activity.metadata['subject'])}</span>}
                      {activity.metadata['duration'] != null && <span>Duration: {formatDuration(Number(activity.metadata['duration']))}</span>}
                      {activity.type.startsWith('appointment_') && (() => {
                        const when = visitWhen(activity.metadata)
                        return when ? <span>When: {when}</span> : null
                      })()}
                      {activity.type === 'call_made' && (() => {
                        const recId = recordingIdForCall(activity, calls)
                        return recId ? <RecordingPlayer companyId={companyId} recordingId={recId} /> : null
                      })()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </li>
        ))}
      </ul>

      {hasNextPage && (
        <div ref={sentinelRef} className="flex justify-center py-3">
          <button
            onClick={() => fetchNextPage()}
            disabled={isFetchingNextPage}
            className="btn btn-ghost btn-sm text-gray-500"
          >
            {isFetchingNextPage ? 'Loading…' : 'Load more'}
          </button>
        </div>
      )}
    </div>
  )
}

